  // src/services/api.js
  // All API calls mapped to the actual PHP backend endpoints



  const BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://capricorn-liquid-protract.ngrok-free.dev/tailorexpert/";
  import { getFCMToken } from "./fcmService";

  function buildAuthHeaders(extra = {}) {
    const headers = { ...extra };
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }

  async function attemptTokenRefresh() {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;
    try {
      const res = await fetch(BASE_URL + "refresh_token.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return false;
      const result = await res.json();
      if (result.status !== "success") return false;
      storeTokens({
        access_token: result.access_token,
        refresh_token: result.refresh_token,
      });
      return true;
    } catch {
      return false;
    }
  }

  // ── Helper: POST with JSON body ────────────────────────────────
  async function postJSON(endpoint, data, retried = false) {
    const res = await fetch(BASE_URL + endpoint, {
      method: "POST",
      headers: buildAuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(data),
    });
    if (res.status === 401 && !retried) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) return postJSON(endpoint, data, true);
      clearSession();
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Helper: GET with query params ─────────────────────────────
  async function getJSON(endpoint, params = {}, retried = false) {
    const qs = new URLSearchParams(params).toString();
    const url = BASE_URL + endpoint + (qs ? `?${qs}` : "");
    const res = await fetch(url, { headers: buildAuthHeaders() });
    if (res.status === 401 && !retried) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) return getJSON(endpoint, params, true);
      clearSession();
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Helper: POST multipart/form-data (file uploads) ───────────
  // Deliberately does NOT set Content-Type: the browser has to generate the
  // multipart boundary itself, and setting the header by hand breaks the upload.
  async function postForm(endpoint, formData, retried = false) {
    const res = await fetch(BASE_URL + endpoint, {
      method: "POST",
      headers: buildAuthHeaders(),
      body: formData,
    });
    if (res.status === 401 && !retried) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) return postForm(endpoint, formData, true);
      clearSession();
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Session helpers ───────────────────────────────────────────
  export function getStoredCompany() {
    try {
      const s = sessionStorage.getItem("oms_company");
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  }

  export function getCmId() {
    return getStoredCompany()?.CM_Id ?? null;
  }

  export function storeCompany(company) {
    sessionStorage.setItem("oms_company", JSON.stringify(company));
  }

  export function getAccessToken() {
    return sessionStorage.getItem("oms_access_token");
  }

  export function getRefreshToken() {
    return sessionStorage.getItem("oms_refresh_token");
  }

  export function storeTokens({ access_token, refresh_token }) {
    sessionStorage.setItem("oms_access_token", access_token);
    sessionStorage.setItem("oms_refresh_token", refresh_token);
  }

  export function clearTokens() {
    sessionStorage.removeItem("oms_access_token");
    sessionStorage.removeItem("oms_refresh_token");
  }

  export function clearSession() {
    sessionStorage.removeItem("oms_company");
    clearTokens();
  }

  export async function getDeviceId() {
    try {
      const token = await getFCMToken();
      localStorage.setItem("fcm_token", token);
      return token;
    } catch (e) {
      localStorage.removeItem("fcm_token");
      console.warn("Could not get Firebase token:", e);
      throw e;
    }
  }
  // Get a persistent installation/device ID
  export function getInstallationDeviceId() {
    let deviceId = localStorage.getItem("device_id");

    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem("device_id", deviceId);
    }

    return deviceId;
  }

  // Fetch Device Details 

  // Get complete device details
  export async function getDeviceDetails() {

    const deviceId = getInstallationDeviceId();
    const fcmToken = await getDeviceId();

    let platform = "web";

    if (typeof navigator !== "undefined") {

      const ua = navigator.userAgent.toLowerCase();

      if (/android/.test(ua)) {
        platform = "android";
      } else if (/iphone|ipad|ipod/.test(ua)) {
        platform = "ios";
      } else if (/windows/.test(ua)) {
        platform = "windows";
      } else if (/mac/.test(ua)) {
        platform = "macos";
      } else if (/linux/.test(ua)) {
        platform = "linux";
      }
    }

    return {
      deviceId,
      fcmToken,
      platform
    };
  }
  // ── DEVICE / SPLASH CHECK ─────────────────────────────────────
  // PHP: Get_Company_Detail.php  POST { CM_Device_id }
  // Returns: { status:"success", data:{ CM_Id, CM_Name, CM_Status,... } }
  //       or { status:"error",   message:"No record found..." }
  export const deviceAPI = {
    checkRegistration: (deviceId) =>
      postJSON("Get_Company_Detail.php", { UD_FCM_Token: deviceId }),
  };

  // ── LICENSE / ACTIVATION (demo or activation expired) ─────────
  // Used by the lock screen shown when CM_Status is 2 (Demo Expired)
  // or 4 (Activation Expired).
  export const licenseAPI = {
    checkStatus: () =>
      postJSON("check_registration_status.php", {}),

    // Submit the key the company received by email to unlock
    // the app. On success the backend sets CM_Status = 3 (Activated)
    // and extends CM_Expiry_Date by one year.
    // PHP: Register_Company_After_Key.php  POST { CM_ID, CM_Key }
    // Returns: { status:"success", message:"App registered successfully" }
    //       or { status:"error",   message:"Keys not matched" }
    activate: ({ cmId, key }) =>
      postJSON("Register_Company_After_Key.php", {
        CM_ID:  cmId,
        CM_Key: key,
      }),
  };

  // ── REGISTER NEW COMPANY ──────────────────────────────────────
  export const registerAPI = {
    // Step 1 – verify email via OTP before creating company
    // PHP: Send_OTP_To_Mail.php  POST { email }
    // Returns: { status:"success", otp: 123456 }
    sendEmailOTP: (email) =>
      postJSON("Send_OTP_To_Mail_Web.php", { email }),

    // Step 2 – create company (called only after OTP verified on frontend)
    // PHP: Set_Company_Detail.php  POST { CM_Name, CM_Address, CM_City, CM_ContactNo, CM_Email, CM_Device_1, CM_User_1 }
    // Returns: { status:"success", data:{ CM_ID, CM_Name, ... } }
   createCompany: async ({
        companyName,
        address,
        city,
        contactNo,
        email,
        userName,
        userEmail,
        password
      }) => {

        // Get device details before registration
        const device = await getDeviceDetails();

        return postJSON("Set_Company_Detail.php", {

          // Company details
          CM_Name: companyName,
          CM_Address: address,
          CM_City: city,
          CM_ContactNo: contactNo,
          CM_Email: email,

          // First company user
          CU_Name: userName,
          CU_Email: userEmail,
          CU_Password: password,

          // Device details
          UD_Device_Id: device.deviceId,
          UD_FCM_Token: device.fcmToken,
          UD_Platform: device.platform

        });
      },
  };

  // ── COMPANY USERS ───────────────────────────────────────────────

  function mapCompanyUser(r) {
    return {
      CU_Id: Number(r.CU_Id ?? 0),
      CM_Id: Number(r.CM_Id ?? 0),
      CU_Name: r.CU_Name ?? "",
      CU_Email: r.CU_Email ?? "",
      CU_Role: r.CU_Role ?? "staff",
      CU_Is_Owner: Number(r.CU_Is_Owner ?? 0),
      CU_Status: Number(r.CU_Status ?? 1),
      CU_Created_At: r.CU_Created_At ?? null,
    };
  }  

    export const usersAPI = {

    // =========================================================
    // FETCH ALL USERS
    // =========================================================
    list: async () => {

      const res = await postJSON(
        "Get_Company_User_List.php",
        {
          CM_Id: getCmId(),
        }
      );

      if (res?.status !== "success") {
        throw new Error(
          res?.message || "Failed to load users"
        );
      }

      return (res.data ?? []).map(mapCompanyUser);
    },


    // =========================================================
    // CREATE NEW USER
    // =========================================================
    create: async (data) => {

      const res = await postJSON(
        "Create_Company_User.php",
        {
          CM_Id: getCmId(),

          CU_Name: data.CU_Name,
          CU_Email: data.CU_Email,
          CU_Password: data.CU_Password,
          CU_Role: data.CU_Role,
        }
      );

      if (res?.status !== "success") {
        throw new Error(
          res?.message || "Failed to create user"
        );
      }

      return res;
    },


    // =========================================================
    // UPDATE EXISTING USER
    // =========================================================
    update: async (data) => {

      const payload = {
        CM_Id: getCmId(),

        CU_Id: data.CU_Id,
        CU_Name: data.CU_Name,
        CU_Email: data.CU_Email,
        CU_Role: data.CU_Role,
        CU_Status: data.CU_Status,
      };

      // Password is optional during edit.
      // If blank, the existing password remains unchanged.
      if (
        data.CU_Password &&
        data.CU_Password.trim() !== ""
      ) {
        payload.CU_Password = data.CU_Password;
      }

      const res = await postJSON(
        "Update_Company_User.php",
        payload
      );

      if (res?.status !== "success") {
        throw new Error(
          res?.message || "Failed to update user"
        );
      }

      return res;
    },


    // =========================================================
    // DELETE USER
    // =========================================================
    delete: async (CU_Id) => {

      const res = await postJSON(
        "Delete_Company_User.php",
        {
          CM_Id: getCmId(),
          CU_Id: CU_Id,
        }
      );

      if (res?.status !== "success") {
        throw new Error(
          res?.message || "Failed to delete user"
        );
      }

      return res;
    },

  };

  // ── CUSTOMERS ─────────────────────────────────────────────────
  function mapCustomer(r) {
    return {
      id:      r.LM_ID,
      name:    r.LM_Name    ?? "",
      phone:   r.LM_ContactNo  ?? "",
      address: r.LM_City     ?? "",
      balance: Number(r.LM_Balance  ?? 0),
    };
  }

  export const customersAPI = {
    list: async () => {
    const res = await postJSON("Get_Ledger_List_Web.php", {
      LM_CM_ID: getCmId(),
      LM_IsKarigar: 0,
    });
    return (res.data ?? []).map(mapCustomer);
  },
    get: async (id) => {
      const res = await postJSON("Get_Ledger_Detail.php", {
        LM_ID: id,
        LM_CM_ID: getCmId(),
      });

      // ✅ MAP RESPONSE
      return {
        customer: {
          id: res.ledgerDetails.LM_ID,
          name: res.ledgerDetails.LM_Name,
          phone: res.ledgerDetails.LM_ContactNo,
          address: res.ledgerDetails.LM_City,
          reference: res.ledgerDetails.LM_ReferenceName,
        },

        products: res.productDetails ?? []
      };
    },
    getOrders: async (id) => {
      const res = await postJSON("Get_Order_List.php", {
        OM_CM_ID: id,
      });
      return res.data ?? [];
    },
    create: (data) => postJSON("Set_Ledger_Detail.php", {
      LM_CM_ID:         getCmId(),
      LM_Name:          data.name,
      LM_ContactNo:     data.phone,
      LM_City:          data.address,
      LM_ReferenceName: data.reference,
      LM_IsKarigar:     0,
    }),
    update: (id, data) => postJSON("Set_Ledger_Detail.php", {
      LM_CM_ID :        getCmId(),
      LM_ID:            id,
      LM_Name:          data.name,
      LM_ContactNo:     data.phone,
      LM_City:          data.address,
      LM_ReferenceName: data.reference,
      LM_IsKarigar : 0
    }),
  };

  
 
  // ── KARIGARS ──────────────────────────────────────────────────

  function mapKarigar(r) {
    return {
      id: r.LM_ID ?? r.lm_id,
      cm_id: r.LM_CM_ID ?? r.lm_cm_id,
      name: r.LM_Name ?? r.lm_name ?? "",
      phone: r.LM_ContactNo ?? r.lm_contactno ?? "",
      workType: r.LM_City ?? r.lm_city ?? "Both",
      speciality: r.LM_ReferenceName ?? r.lm_referencename ?? "",
      balance: Number(r.LM_Balance ?? r.lm_balance ?? 0),
      status: "Active",
      Karigar_Detail: r.Karigar_Detail ?? r.karigar_detail ?? [],
    };
  }

  export const karigarsAPI = {
    list: async () => {
      const res = await postJSON("Get_Ledger_List.php", {
        LM_CM_ID: getCmId(),
        LM_IsKarigar: 1,
      });

      return (res.data ?? []).map(mapKarigar);
    },

    // Return the complete API response, including productDetails.
    get: async (id) => {
      const res = await postJSON("Get_Ledger_Detail.php", {
        LM_ID: id,
        LM_CM_ID: getCmId(),
      });

      if (res?.status !== "success") {
        throw new Error(
          res?.message || "Failed to load karigar details."
        );
      }

      return res;
    },

    create: (data) =>
      postJSON("Set_Ledger_Detail.php", {
        LM_CM_ID: getCmId(),
        LM_Name: data.name,
        LM_ContactNo: data.phone,
        LM_City: data.workType,
        LM_ReferenceName: data.speciality,
        LM_IsKarigar: 1,
        Karigar_Detail: data.Karigar_Detail ?? [],
      }),

    update: (id, data) =>
      postJSON("Set_Ledger_Detail.php", {
        LM_CM_ID: getCmId(),
        LM_ID: id,
        LM_Name: data.name,
        LM_ContactNo: data.phone,
        LM_City: data.workType,
        LM_ReferenceName: data.speciality,
        LM_IsKarigar: 1,
        Karigar_Detail: data.Karigar_Detail ?? [],
      }),

    delete: (id) =>
      postJSON("Delete_Ledger.php", {
        LM_ID: id,
        LM_CM_ID: getCmId(),
      }),
  };


  // ── KARIGAR WORK (assign / return) ────────────────────────────
  // The karigar lifecycle lives on order_stich_detail:
  //   OSD_K_LM_ID = 0                          → not assigned to anyone yet
  //   OSD_K_IsIssued = 1  + OSD_K_IssueDate    → issued to a karigar
  //   OSD_K_IsReceived = 1 + OSD_K_ReceiveDate → returned by the karigar
  //   OSD_IsDelivered = 1                      → handed to the customer (OrderDetail)
  //
  // Filenames are as they exist on the server. Two carry unusual spelling — "Asign"
  // (one s) and "LIst" (capital I) — kept verbatim because the host filesystem is
  // case-sensitive and "correcting" either one returns a 404.
  const EP_ASSIGN_LIST = "Get_Stiching_List_To_Asign.php";
  const EP_ASSIGN_SET  = "Assign_To_Karigar.php";
  const EP_RETURN_LIST = "Get_Stiching_LIst_Of_Karigar.php";
  const EP_RETURN_SET  = "Return_From_Karigar.php";

  // A stitch item nobody is working on yet.
  function mapAssignableWork(r) {
    return {
      osdId:        r.OSD_ID,
      orderId:      r.OM_ID,
      orderNo:      r.OM_No,
      orderDate:    r.OM_Date,
      deliveryDate: r.OSD_DeliveryDate,
      customerName: r.OM_Customer  ?? "",
      // The stitch row carries its own ledger id, so the garment can be for someone
      // other than the person who placed the order (a family member, typically).
      forName:      r.OSD_Customer ?? "",
      product:      r.PM_Name      ?? "",
      remarks:      r.OSD_Remarks  ?? "",
      // Not in the current SELECT. Present only if OSD.OSD_PM_ID gets added to it, which
      // is what would let AssignWork pre-check rates instead of relying on the rollback.
      pmId:         r.OSD_PM_ID ?? r.PM_ID ?? null,
    };
  }

  // A stitch item currently out with a karigar.
  function mapIssuedWork(r) {
    return {
      osdId:        r.OSD_ID,
      orderNo:      r.OM_No,
      orderDate:    r.OM_Date,
      customerId:   r.OM_LM_ID,
      customerName: r.LM_Name ?? "",
      issueDate:    r.OSD_K_IssueDate,
      product:      r.PM_Name ?? "",
    };
  }

  // All four endpoints answer HTTP 200 even when they fail, reporting it as
  // status:"error" — the same convention as Remove_Order.php — so postJSON's HTTP check
  // never fires and the status has to be read explicitly.
  function unwrap(res, fallback) {
    if (res?.status !== "success") {
      console.error(`[karigarWorkAPI] ${fallback}:`, res);
      throw new Error(res?.message || res?.msg || fallback);
    }
    return res;
  }

  // The write endpoints wrap the whole KarigarDetail array in one transaction, so send a
  // single batched call. Looping per item would let a mid-way failure leave half the
  // selection issued.
  function buildBatch(osdIds, karigarId, dateKey, dateValue) {
    const ids = (Array.isArray(osdIds) ? osdIds : [osdIds])
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0);

    if (ids.length === 0)     throw new Error("No valid items selected");
    if (!Number(karigarId))   throw new Error("Select a karigar first");
    if (!dateValue)           throw new Error("Pick a date first");

    return ids.map((OSD_ID) => ({
      OSD_ID,
      OSD_K_LM_ID: Number(karigarId),
      [dateKey]:   dateValue,
    }));
  }

  export const karigarWorkAPI = {
    // Every unassigned stitch item across all open orders.
    // NOTE: this endpoint keys on OM_CM_ID; the other three use OSD_CM_ID.
    pendingToAssign: async () => {
      const res = await postJSON(EP_ASSIGN_LIST, { OM_CM_ID: getCmId() });
      unwrap(res, "Failed to load work pending assignment");
      return (res.data ?? []).map(mapAssignableWork);
    },

    // What one karigar still owes back.
    pendingToReturn: async (karigarId) => {
      if (!Number(karigarId)) return [];
      const res = await postJSON(EP_RETURN_LIST, {
        OSD_CM_ID:   getCmId(),
        OSD_K_LM_ID: Number(karigarId),
      });
      unwrap(res, "Failed to load this karigar's pending work");
      return (res.data ?? []).map(mapIssuedWork);
    },

    // Issue a batch to one karigar. The endpoint reads each item's rate from
    // karigar_rate_detail and refuses the WHOLE batch if any product has no rate for
    // that karigar, so callers must surface the thrown message rather than a generic one.
    assign: async ({ karigarId, issueDate, osdIds }) => {
      const res = await postJSON(EP_ASSIGN_SET, {
        OSD_CM_ID:     getCmId(),
        KarigarDetail: buildBatch(osdIds, karigarId, "OSD_K_IssueDate", issueDate),
      });
      return unwrap(res, "Failed to assign work");
    },

    // Take a batch back. This endpoint reports success in `msg`, not `message`.
    receive: async ({ karigarId, receiveDate, osdIds }) => {
      const res = await postJSON(EP_RETURN_SET, {
        OSD_CM_ID:     getCmId(),
        KarigarDetail: buildBatch(osdIds, karigarId, "OSD_K_ReceiveDate", receiveDate),
      });
      return unwrap(res, "Failed to receive work");
    },
  };

  // ── ORDERS ────────────────────────────────────────────────────
  function mapOrder(o) {
    return {
      id:         o.OM_ID,
      customerId: o.OM_LM_ID,
       customerName: o.LM_Name ?? "",
      orderDate:  o.OM_Date,
      amount:     Number(o.OM_TotalReceivable  ?? 0),
      discount:   Number(o.OM_Discount         ?? 0),
      // Remove_Order.php refuses to delete an order once any payment is recorded,
      // so the list needs this to disable the delete button up front.
      received:   Number(o.OM_TotalReceipt     ?? 0),
      remarks:    o.OM_Remarks  ?? "",
      status: Number(o.OM_IsClosed) === 1 ? "Completed" : "Pending",
    };
  }


  export const ordersAPI = {
    list: async () => {
    const res = await postJSON("Get_Order_List.php", {
      OM_CM_ID : getCmId(),
    });
    return (res.Order_Master ?? []).map(mapOrder);
  },
  get: async (id) => {
    const res = await postJSON("Get_Order.php", {
      OM_ID: id,
      OM_CM_ID : getCmId()
    });

    const o = res.Order_Master;

    if (!o) return null;

    return {
      id: o.OM_ID,
      customerId: o.OM_LM_ID,
      customerName: o.LM_Name ?? "",
      orderDate: o.OM_Date,
      deliveryDate: o.OM_Delivery_Date,

      status: Number(o.OM_IsClosed) === 1 ? "Completed" : "Pending",

      amount: Number(o.OM_TotalReceivable ?? 0),
      discount: Number(o.OM_Discount ?? 0),
      remarks: o.OM_Remarks ?? "",

      assignedKarigarId: res.Order_Stich_Detail?.[0]?.OSD_K_LM_ID ?? null,
      assignedKarigarName: res.Order_Stich_Detail?.[0]?.OSD_K_Name ?? null,

      stitchDetails: (res.Order_Stich_Detail ?? []).map(s => ({
        id: s.OSD_ID,
        type: s.PM_Name,
        qty: s.OSD_Qty,
        rate: Number(s.OSD_Rate),
        notes: s.OSD_Remarks,
         karigarId: s.OSD_K_LM_ID,
        karigarName: s.OSD_K_Name,

        karigarIssued: s.OSD_K_IsIssued,
        karigarReceived: s.OSD_K_IsReceived,

        // Delivered-to-customer is OSD_IsDelivered — the column Deliver_To_Customer.php
        // sets to 1. Deliberately NOT Delivery_Status: that is a computed stage code the
        // backend returns as 1 for a brand-new stitch, so reading it as a boolean made
        // every freshly-added item render as "Delivered".
        isDelivered: Number(s.OSD_IsDelivered ?? 0) === 1,
      })),

      materialDetails: (res.Order_Material_Detail ?? []).map(m => ({
        id: m.OMD_ID,
        name: m.MM_Name,
        quantity: Number(m.OMD_Qty),
        rate: Number(m.OMD_Rate),
        notes: m.OMD_Remarks,
        unit: m.OMD_Unit
      })),
    };
  },
 
    create: async (data) => {
      const orderRes = await postJSON("Set_New_Order_Web.php", {
        OM_CM_ID:           getCmId(),
        OM_LM_ID:           data.customerId,
        OM_TotalReceivable: 0,
        OM_Discount:        data.discount,
        OM_Remarks:         data.remarks,
        OM_Date:            data.orderdate,
        OM_Delivery_Date :  data.deliveryDate,
        OM_TotalReceipt :   0,
        OM_IsClosed :       0
      });
      const orderId =
        orderRes?.data?.OM_ID  ||
        orderRes?.OM_ID  ||
        orderRes?.data?.[0]?.OM_ID  ||
        null;

      if (!orderId) {
        console.error("Invalid order response:", orderRes);
        throw new Error("Order ID not found");
      }
      for (const s of data.stitchDetails   ?? []) {
        await postJSON("Add_Order_Stich_Detail.php",    { osd_om_id: orderId, osd_pm_id: s.pm_id, osd_qty: s.qty, osd_rate: s.rate });
      }
      for (const m of data.materialDetails ?? []) {
        await postJSON("Add_Order_Material_Detail.php", { omd_om_id: orderId, omd_mm_id: m.mm_id, omd_qty: m.quantity, omd_rate: m.rate });
      }
      return orderRes;
    },
    
     addStitch: async (payload) => {
    const res = await postJSON("Add_Order_Stich_Detail_Web.php", {
      OSD_CM_ID: getCmId(),
      OSD_OM_ID: payload.OSD_OM_ID,
      OSD_PM_ID: payload.OSD_PM_ID,
      OSD_LM_ID: payload.OSD_LM_ID,
    });

    if (res?.status !== "success") {
      console.error("Add Stitch Error:", res);
      throw new Error(res?.message || "Failed to add stitch");
    }

    return res;
  },

  addMaterial: async (payload) => {
        const res = await postJSON("Add_Order_Material_Detail_Web.php", {
          OMD_CM_ID: getCmId(),
          OMD_OM_ID: payload.OMD_OM_ID,
          OMD_MM_ID: payload.OMD_MM_ID,
          OMD_Qty: payload.OMD_Qty,
          OMD_Rate:payload.OMD_Rate,
          OMD_Unit : payload.OMD_Unit
          
        });

        if (res?.status !== "success") {
          console.error("Add material Error:", res);
          throw new Error(res?.message || "Failed to add material");
        }

        return res;
      },

      deleteMaterial: async (id) => {
        const res = await postJSON("Remove_Order_Material_Detail.php", {
          OMD_ID : id
        });

        if (res?.status !== "success") {
          console.error("delete material Error:", res);
          throw new Error(res?.message || "Failed to delete material");
        }

        return res;
      },
      deleteStitch: async (id) => {
        const res = await postJSON("Remove_Order_Stich_Detail.php", {
          OSD_ID : id
        });

        if (res?.status !== "success") {
          console.error("delete stitch Error:", res);
          throw new Error(res?.message || "Failed to delete stitch");
        }

        return res;
      },

      // Mark stitch items as delivered to the customer (sets OSD_IsDelivered = 1).
      // Deliver_To_Customer.php takes the whole selection as an OSD_ID array and wraps it
      // in a single transaction, so send one batched call — looping per item would let a
      // mid-way failure leave the order half-delivered.
      deliverItems: async (ids) => {
        const OSD_ID = (Array.isArray(ids) ? ids : [ids])
          .map(Number)
          .filter((n) => Number.isFinite(n) && n > 0);

        if (OSD_ID.length === 0) throw new Error("No valid items selected to deliver");

        const res = await postJSON("Deliver_To_Customer.php", { OSD_ID });

        if (res?.status !== "success") {
          console.error("deliver to customer Error:", res);
          throw new Error(res?.message || "Failed to deliver items");
        }

        return res;
      },
      // Delete a whole order (cascades material + stitch rows, then order_master).
      // Remove_Order.php reports business refusals — order closed, payment received,
      // partially delivered, karigar assigned — as HTTP 200 with status:"error" and a
      // numeric `error` code, so the status check below is what surfaces them.
      delete: async (id) => {
        const res = await postJSON("Remove_Order.php", {
          OM_ID:    id,
          OM_CM_ID: getCmId(),
        });

        if (res?.status !== "success") {
          console.error("delete order Error:", res);
          throw new Error(res?.message || "Failed to delete order");
        }

        return res;
      },
  };

  // Products API-----------------------------------------------

  export const StitchAPI ={
    get : async () => {
      const stitch = await postJSON("Get_Product_List.php",{PM_CM_ID : getCmId() , Flag :1});
      return { stitchTypes : stitch.data ?? []}
    }
  }
  export const MaterialAPI ={
    get : async () => {
      const material = await postJSON("Get_Material_List.php",{MM_CM_ID : getCmId() });
      return { materialTypes : material.data ?? []}
    },
     
    
  }


  // ── SETTINGS ──────────────────────────────────────────────────
 export const settingsAPI = {
  get: async () => {
    try {
      const products = await postJSON("Get_Product_List.php", {
        PM_CM_ID: getCmId(),
        Flag: 0,
      });

      let materials = { data: [] };

      try {
        materials = await postJSON("Get_Material_List.php", {
          MM_CM_ID: getCmId(),
        });
      } catch (e) {
        console.warn("Material API failed");
      }

      return {
        stitchTypes: products?.data ?? [],
        materialTypes: materials?.data ?? [],
      };

    } catch (e) {
      console.error("settingsAPI failed:", e);

      return {
        stitchTypes: [],
        materialTypes: [],
      };
    }
  },

  addStitchType: (data) =>
    postJSON("Set_Product.php", {
      PM_CM_ID: getCmId(),
      data: [
        {
          PM_IsHide: data.PM_IsHide ?? 0,
          PM_StichRate: data.PM_StichRate ?? 0,
          PM_Name: data.PM_Name,
          PM_M1: data.PM_M1 ?? "",
          PM_M2: data.PM_M2 ?? "",
          PM_M3: data.PM_M3 ?? "",
          PM_M4: data.PM_M4 ?? "",
          PM_M5: data.PM_M5 ?? "",
          PM_M6: data.PM_M6 ?? "",
          PM_M7: data.PM_M7 ?? "",
          PM_M8: data.PM_M8 ?? "",
          PM_M9: data.PM_M9 ?? "",
          PM_M10: data.PM_M10 ?? "",
          PM_M11: data.PM_M11 ?? "",
          PM_M12: data.PM_M12 ?? "",
        },
      ],
    }),

  updateStitchType: (data) =>
    postJSON("Set_Product.php", {
      PM_CM_ID: getCmId(),
      data: [
        {
          PM_ID: data.PM_ID,
          PM_IsHide: data.PM_IsHide ?? 0,
          PM_StichRate: data.PM_StichRate ?? 0,
          PM_Name: data.PM_Name,
          PM_M1: data.PM_M1 ?? "",
          PM_M2: data.PM_M2 ?? "",
          PM_M3: data.PM_M3 ?? "",
          PM_M4: data.PM_M4 ?? "",
          PM_M5: data.PM_M5 ?? "",
          PM_M6: data.PM_M6 ?? "",
          PM_M7: data.PM_M7 ?? "",
          PM_M8: data.PM_M8 ?? "",
          PM_M9: data.PM_M9 ?? "",
          PM_M10: data.PM_M10 ?? "",
          PM_M11: data.PM_M11 ?? "",
          PM_M12: data.PM_M12 ?? "",
        },
      ],
    }),

  deleteStitchType: (item) =>
    postJSON("Delete_Product.php", {
      PM_CM_ID: getCmId(),
      PM_ID: item.PM_ID,
    }),

  addMaterialType: (data) =>
    postJSON("Set_Material_Master.php", {
      MM_ID: data.MM_ID ?? 0,
      MM_CM_ID: getCmId(),
      MM_Name: data.MM_Name,
      MM_Unit: data.MM_Unit,
    }),

  deleteMaterialType: (item) =>
    postJSON("Delete_Material.php", {
      MM_ID: item.MM_ID,
    }),
};

  // ── MEASUREMENTS ──────────────────────────────────────────────
  // NOTE: filenames below are inferred from the table name
  // (Ledger_Measurment_Master) since the exact filenames weren't given —
  // confirm/rename these two lines if your server uses different names.
  // Get_Ledger_Measurment_Master.php returns one product joined with this
  // customer's saved measurement for it (LMM_* fields are null/absent if
  // the customer has no saved measurement for that product yet).
  // Set_Ledger_Measurment_Detail.php inserts (LMM_ID = 0) or updates
  // (LMM_ID = existing id) that measurement record.
  export const measurementAPI = {
    get: (cmId, ledgerId, productId) =>
      postJSON("Get_Measurement_Detail.php", {
        LMM_CM_ID: cmId,
        LMM_LM_ID: ledgerId,
        LMM_PM_ID: productId,
      }),
    save: (payload) =>
      postJSON("Set_Measurement_Detail.php", {
        LMM_CM_ID:   payload.cmId,
        LMM_ID:      payload.lmmId ?? 0,
        LMM_LM_ID:   payload.ledgerId,
        LMM_PM_ID:   payload.productId,
        LMM_M1:      payload.values?.[0] ?? "",
        LMM_M2:      payload.values?.[1] ?? "",
        LMM_M3:      payload.values?.[2] ?? "",
        LMM_M4:      payload.values?.[3] ?? "",
        LMM_M5:      payload.values?.[4] ?? "",
        LMM_M6:      payload.values?.[5] ?? "",
        LMM_M7:      payload.values?.[6] ?? "",
        LMM_M8:      payload.values?.[7] ?? "",
        LMM_M9:      payload.values?.[8] ?? "",
        LMM_M10:     payload.values?.[9] ?? "",
        LMM_M11:     payload.values?.[10] ?? "",
        LMM_M12:     payload.values?.[11] ?? "",
        LMM_Remarks: payload.remarks ?? "",
      }),
  };

  // ── PAYMENTS / RECEIPTS / COMPANY ─────────────────────────────
  // Follows the Tally convention the shop already thinks in:
  //   PAYMENT = money paid OUT   → to a karigar      (payment_detail, PD_*)
  //   RECEIPT = money received IN → from a customer  (receipt_detail, RD_*)
  //
  // The two LIST endpoints are written and live in docs/backend/.
  //
  // Both SET filenames are the server's, verbatim. "Get_Receipt.php" is an INSERT
  // despite the Get_ prefix — do not confuse it with Get_Receipt_List.php next to it.
  const EP_PAYMENT_LIST   = "Get_Payment_List.php";
  const EP_PAYMENT_SET    = "Karigar_Payment.php";
  const EP_RECEIPT_LIST   = "Get_Receipt_List.php";
  const EP_RECEIPT_SET    = "Get_Receipt.php";         // yes, really — this one writes
  const EP_COMPANY_UPDATE = "update_company.php";  // CONFIRM (reuse register EP w/ CM_ID, or a new one)
  const EP_IMAGE_UPLOAD   = "Upload_Image.php";        // see docs/CPANEL_IMAGE_UPLOAD.md

  // Both list endpoints take an optional inclusive date range. Omitting a bound means
  // "unbounded", so an empty date input and an absent filter behave identically.
  function dateRange({ fromDate, toDate } = {}) {
    const range = {};
    if (fromDate) range.FromDate = fromDate;
    if (toDate)   range.ToDate   = toDate;
    return range;
  }

  // Karigar payment (money paid out to a karigar).
  // "Kasar" is the amount written off on settlement — a real column, so it's carried
  // through rather than folded into the amount.
  function mapPayment(r) {
    return {
      id:          r.PD_ID,
      karigarId:   r.PD_K_LM_ID,
      karigarName: r.PD_Karigar ?? "",
      amount:      Number(r.PD_Amount ?? 0),
      kasar:       Number(r.PD_Kasar  ?? 0),
      mode:        r.PD_Type    ?? "",
      date:        r.PD_Date    ?? null,
      remarks:     r.PD_Remarks ?? "",
    };
  }

  // Order receipt (money received from a customer against an order).
  // The joins on the endpoint are LEFT, so orderNo/customerName can be null when the
  // order or ledger row was deleted — the receipt itself still shows up.
  function mapReceipt(r) {
    return {
      id:           r.RD_ID,
      orderId:      r.RD_OM_ID,
      orderNo:      r.OM_No ?? null,
      customerId:   r.RD_LM_ID,
      customerName: r.RD_Customer ?? "",
      amount:       Number(r.RD_Amount ?? 0),
      kasar:        Number(r.RD_Kasar  ?? 0),
      mode:         r.RD_Type    ?? "",
      date:         r.RD_Date    ?? null,
      remarks:      r.RD_Remarks ?? "",
    };
  }

  export const paymentsAPI = {
    // NOTE: PD_CM_ID has no matching column — payment_detail carries no company id, so
    // the endpoint resolves it through ledger_master via PD_K_LM_ID. The request field
    // is named for symmetry with the receipt endpoint; see the header comment in
    // docs/backend/Get_Payment_List.php.
    list: async (range) => {
      const res = await postJSON(EP_PAYMENT_LIST, {
        PD_CM_ID: getCmId(),
        ...dateRange(range),
      });
      if (res?.status !== "success") {
        console.error("[paymentsAPI] list:", res);
        throw new Error(res?.message || "Failed to load payments");
      }
      return (res.data ?? []).map(mapPayment);
    },
    // Karigar_Payment.php takes NO company id — payment_detail has no CM_ID column and
    // the endpoint validates nothing beyond PD_K_LM_ID > 0. Isolation holds in practice
    // because the karigar is always picked from a company-scoped list, but the server
    // cannot enforce it. Side effect: LM_Balance = LM_Balance - PD_Amount + PD_Kasar.
    create: async (d) => {
      const res = await postJSON(EP_PAYMENT_SET, {
        PD_K_LM_ID: Number(d.karigarId),
        PD_Date:    d.date,
        PD_Amount:  Number(d.amount ?? 0),
        PD_Kasar:   Number(d.kasar  ?? 0),
        PD_Type:    d.mode    ?? "",
        PD_Remarks: d.remarks ?? "",
      });
      // 200-with-status:"error" is the house convention, so the HTTP check never fires.
      if (res?.status !== "success") {
        console.error("[paymentsAPI] create:", res);
        throw new Error(res?.message || "Failed to save payment");
      }
      return res;
    },
  };

  export const receiptsAPI = {
    list: async (range) => {
      const res = await postJSON(EP_RECEIPT_LIST, {
        RD_CM_ID: getCmId(),
        ...dateRange(range),
      });
      if (res?.status !== "success") {
        console.error("[receiptsAPI] list:", res);
        throw new Error(res?.message || "Failed to load receipts");
      }
      return (res.data ?? []).map(mapReceipt);
    },
    // Get_Receipt.php (an insert, despite the name) also updates the order:
    // OM_TotalReceipt += Amount, OM_Discount += Kasar, and closes it once the running
    // total covers OM_TotalReceivable — so the caller must refetch the order afterwards
    // rather than patch its numbers locally.
    create: async (d) => {
      const res = await postJSON(EP_RECEIPT_SET, {
        RD_CM_ID:   getCmId(),
        RD_OM_ID:   Number(d.orderId),
        RD_LM_ID:   Number(d.customerId ?? 0) || 0,
        RD_Date:    d.date,
        RD_Amount:  Number(d.amount ?? 0),
        RD_Kasar:   Number(d.kasar  ?? 0),
        RD_Type:    d.mode    ?? "",
        RD_Remarks: d.remarks ?? "",
      });
      if (res?.status !== "success") {
        console.error("[receiptsAPI] create:", res);
        throw new Error(res?.message || "Failed to save receipt");
      }
      return res;
    },
  };

  // ── IMAGE UPLOAD ──────────────────────────────────────────────
  // Uploads the file to the cPanel host and returns the RELATIVE path the DB stores
  // (e.g. "uploads/company/12/logo_ab12cd.jpg"). Relative keeps the column short and
  // survives a domain change; branding.resolveImageUrl() turns it back into an
  // absolute URL at render time. See docs/CPANEL_IMAGE_UPLOAD.md for the backend.

  
  export const uploadAPI = {
    // kind: "logo" | "profile"
    image: async (blob, kind, filename) => {
      const fd = new FormData();
      fd.append("file", blob, filename || `${kind}.jpg`);
      fd.append("CM_ID", getCmId() ?? "");
      fd.append("type", kind);

      const res = await postForm(EP_IMAGE_UPLOAD, fd);

      if (res?.status !== "success" || !res?.path) {
        console.error("[uploadAPI.image] upload failed:", res);
        throw new Error(res?.message || "Image upload failed");
      }
      return res.path;
    },
  };

  export const companyAPI = {
    // CM_Logo / CM_ProfilePic carry the relative upload path returned by
    // uploadAPI.image (or "" to clear). Legacy rows may still hold a base64 data URL —
    // resolveImageUrl() in utils/branding.js handles both.
    update: (d) =>
      postJSON(EP_COMPANY_UPDATE, {
        CM_ID:         getCmId(),
        CM_Name:       d.name,
        CM_Address:    d.address,
        CM_City:       d.city,
        CM_ContactNo:  d.contact,
        CM_Email:      d.email,
        CM_User:       d.user,
        CM_Logo:       d.logo,        // upload path, or "" when cleared
        CM_ProfilePic: d.profilePic,  // upload path, or "" when cleared
      }),
  };

  // ── LOGOUT ────────────────────────────────────────────────────
  export async function logoutAPI() {
    try { await fetch(BASE_URL + "logout.php", { method: "POST" }); } catch {}
    clearSession();
  }

  // ── DASHBOARD ─────────────────────────────────────────────────
  // No dedicated dashboard PHP — derive stats by calling existing list APIs
  export const dashboardAPI = {
    stats: async () => {
      try {
        const cm_id = getCmId();
        const [custRes, kariRes, orderRes] = await Promise.all([
          postJSON("Get_Ledger_List.php",  { LM_CM_ID : cm_id, LM_IsKarigar: 0 }),
          postJSON("Get_Ledger_List.php",  { LM_CM_ID :cm_id, LM_IsKarigar: 1 }),
          postJSON("Get_Order_List.php",   { OM_CM_ID : cm_id}),
        ]);

        const orders = orderRes.Order_Master  ?? [];
        return {
          totalCustomers:   (custRes.data  ?? []).length,
          totalKarigars:    (kariRes.data  ?? []).length,
          totalOrders:      orders.length,
          pendingOrders:    orders.filter(o => Number(o.OM_IsClosed) === 0).length,
          completedOrders:  orders.filter(o => Number(o.OM_IsClosed) === 1).length,

          // optional for now
          inProgressOrders: 0,
        };
      } catch (e) {
        console.error("[dashboardAPI.stats]", e);
        return { totalCustomers: 0, totalKarigars: 0, totalOrders: 0, pendingOrders: 0, inProgressOrders: 0, completedOrders: 0 };
      }
    },

    recentOrders: async () => {
      try {
        const LM_CM_ID = getCmId();
        const cm_id = getCmId();
        // Fetch orders + customers together
        const [orderRes, custRes] = await Promise.all([
          postJSON("Get_Order_List.php",  { OM_CM_ID  : cm_id }),
          postJSON("Get_Ledger_List.php", { LM_CM_ID : cm_id, LM_CM_ID: 0 }),
        ]);
        // const customers = {};
        // (custRes.data ?? []).forEach(c => { customers[c.lm_id] = c.lm_name; });

        return (orderRes.Order_Master ?? [])
          .slice(0, 10)
          .map(o => ({
            id:                  o.OM_ID,
            customerName:        o.LM_Name ?? "—",
            orderDate:           o.OM_Date,
            deliveryDate:        o.OM_Delivery_Date ?? null,
            status:              o.OM_IsClosed ? "Completed" :"In Progress" ,
            assignedKarigarName: o.om_karigar_name ?? null,
            amount : o.OM_TotalReceivable
          }));
      } catch (e) {
        console.error("[dashboardAPI.recentOrders]", e);
        return [];
      }
    },
  };

  // -----------------------------------Logout --------------------------
// services/api.js

export const authAPI = {
  login: async ({ email, password }) => {
    const device = await getDeviceDetails();

    return postJSON("login.php", {
      CU_Email: email,
      CU_Password: password,

      UD_Device_Id: device.deviceId,
      UD_FCM_Token: device.fcmToken,
      UD_Platform: device.platform
    });
  },

  logout: async () => {

      const fcmToken = localStorage.getItem("fcm_token");

      if (!fcmToken) {
          throw new Error("FCM token not found.");
      }

      const result = await postJSON("logout.php", {
          UD_FCM_Token: fcmToken
      });

      if (result.status === "success") {
          localStorage.removeItem("fcm_token");
      }

      return result;
  }
};
